import { RingBuffer } from './ring-buffer'

export interface Breadcrumb {
  action: string
  target?: string
  ts: number
}

interface Options {
  capacity: number
  now?: () => number
}

export interface BreadcrumbTrail {
  record(entry: { action: string; target?: string }): void
  snapshot(): Breadcrumb[]
  droppedCount(): number
}

/**
 * A short trail of what the user did before they pressed the shortcut.
 *
 * This is the "what were you doing" context that AGI gets from its Pendo event
 * buffer. PF-Connect has no `track()` calls today, and Pendo's own captured
 * data is not readable back from the browser in any case — its agent reports to
 * Pendo, not to the page. So the trail is its own thing, with a deliberately
 * source-agnostic entry shape: Pendo calls can be teed into it later without
 * reshaping anything already stored.
 *
 * Entries are descriptions, never state snapshots. The undo stack holds fifty
 * unshared deep copies of the document — on a large model that is hundreds of
 * megabytes, for the same signal a few kilobytes of action names carry.
 */
export function createBreadcrumbTrail(options: Options): BreadcrumbTrail {
  const { capacity, now = (): number => Date.now() } = options
  const buffer = new RingBuffer<Breadcrumb>(capacity)

  return {
    record: (entry): void => {
      buffer.push({
        action: entry.action,
        ...(entry.target !== undefined && { target: entry.target }),
        ts: now(),
      })
    },
    snapshot: (): Breadcrumb[] => buffer.snapshot(),
    droppedCount: (): number => buffer.droppedCount(),
  }
}
